/**
 * API 전자책 서비스
 * 외부 API를 사용하여 전자책 페이지 데이터를 제공합니다.
 */

import apiClient from '../../core/http/axios';

export interface ApiPageComponent {
  id: string;
  component_no: number;
  kind: 'subtitle' | 'title' | 'paragraph' | 'figure' | 'header_title' | 'header_page_number' | 'table' | 'footnote';
  content: string;
  media_urls: string;
  media_note: string;
  index: number;
}

export interface ApiPageData {
  page_no: number;  // DB 인덱스 (0부터 시작)
  idx: number;      // 실제 페이지 번호
  display_number: number;
  img_url: string;
  md_text: string;
  components: ApiPageComponent[];
  last_page_index?: number;  // 마지막 페이지 인덱스 (pages-info API에서 제공)
}

export interface ApiPageResponse {
  success: boolean;
  error: string | null;
  data: ApiPageData;
  code: string | null;
}

// 여러 페이지 조회 응답
export interface ApiPagesInfoResponse {
  success: boolean;
  error: string | null;
  data: ApiPageData[];
  code: string | null;
}

// 진행 상태 응답
export interface ApiProgressResponse {
  success: boolean;
  error: string | null;
  data: {
    book_uuid: string;
    page_idx: number;
  };
  code: string | null;
}

// book-info API 응답 타입
export interface ApiBookInfoResponse {
  success: boolean;
  error: string | null;
  data: {
    book_uuid: string;
    title: string;
    author: string;
    img_url: string;
    total_page: number;
    parsed_page: number;
    min_page_no: number;
    max_page_no: number;
    [key: string]: any;
  };
  code: string | null;
}

// 페이지 로드 설정
export const PAGE_LOAD_CONFIG = {
  DEFAULT_START_PAGE: 1,      // 기본 시작 페이지
  INITIAL_PAGES_TO_LOAD: 8,   // 초기에 로드할 페이지 수 (레거시 - 현재 전체 로드 사용)
  BATCH_SIZE: 30,             // 한 번에 요청할 최대 페이지 수 (ERR_INCOMPLETE_CHUNKED_ENCODING 방지)
};

/**
 * API 전자책 서비스
 */
export const bookService = {
  /**
   * 특정 책의 단일 페이지 정보를 가져옵니다. (기존 API - 호환성 유지)
   * @param bookUid 책의 고유 ID
   * @param pageIndex 페이지 인덱스 (0부터 시작)
   */
  getPageInfo: async (
    bookUid: string, 
    pageIndex: number
  ): Promise<ApiPageData> => {
    // console.log('[API] getPageInfo Request:', {
    //   book_uuid: bookUid,
    //   page_index: pageIndex,
    // });
    
    try {
      const response = await apiClient.get<ApiPageResponse>(
        `/viewer/page-info`,
        {
          params: {
            book_uuid: bookUid,
            page_index: pageIndex,
          },
          headers: {
            'accept': 'application/json',
          },
        }
      );

      // console.log('[API] getPageInfo Response:', {
      //   success: response.data.success,
      //   error: response.data.error,
      //   code: response.data.code,
      //   hasData: !!response.data.data,
      // });

      if (!response.data.success) {
        throw new Error(response.data.error || '페이지 정보를 가져오는데 실패했습니다.');
      }

      const data = response.data.data;
      
      // components가 없으면 빈 배열로 초기화
      if (!data.components) {
        // console.warn('[API] components가 없습니다. 빈 배열로 초기화합니다.');
        data.components = [];
      }
      
      return data;
    } catch (error: any) {
      // console.error('[API] getPageInfo failed:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },

  /**
   * 여러 페이지 정보를 한 번에 가져옵니다. (새 API)
   * @param bookUid 책의 고유 ID
   * @param startPageIndex 시작 페이지 인덱스
   * @param endPageIndex 끝 페이지 인덱스
   * @returns 페이지 배열과 마지막 페이지 인덱스
   */
  getPagesInfo: async (
    bookUid: string,
    startPageIndex: number,
    endPageIndex: number
  ): Promise<{ pages: ApiPageData[]; lastPageIndex: number }> => {
    // console.log('[API] getPagesInfo Request:', {
    //   book_uuid: bookUid,
    //   start_page_index: startPageIndex,
    //   end_page_index: endPageIndex,
    // });

    try {
      const response = await apiClient.get<ApiPagesInfoResponse>(
        `/viewer/pages-info`,
        {
          params: {
            book_uuid: bookUid,
            start_page_index: startPageIndex,
            end_page_index: endPageIndex,
          },
          headers: {
            'accept': 'application/json',
          },
        }
      );

      // console.log('[API] getPagesInfo Response:', {
      //   success: response.data.success,
      //   error: response.data.error,
      //   code: response.data.code,
      //   pageCount: response.data.data?.length || 0,
      // });

      if (!response.data.success) {
        throw new Error(response.data.error || '페이지 정보를 가져오는데 실패했습니다.');
      }

      const pages = response.data.data || [];
      
      // 각 페이지의 components가 없으면 빈 배열로 초기화
      pages.forEach(page => {
        if (!page.components) {
          page.components = [];
        }
      });
      
      // last_page_index는 첫 번째 페이지 데이터에서 가져옴 (모든 페이지에 동일한 값)
      const lastPageIndex = pages[0]?.last_page_index ?? endPageIndex;
      
      return { pages, lastPageIndex };
    } catch (error: any) {
      // console.error('[API] getPagesInfo failed:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },

  /**
   * 책 정보를 가져옵니다. (total_page 등)
   * @param bookUid 책의 고유 ID
   */
  getBookInfo: async (
    bookUid: string
  ): Promise<{ totalPage: number; myPageIdx: number }> => {
    try {
      const response = await apiClient.get<ApiBookInfoResponse>(
        `/viewer/book-info`,
        {
          params: { book_uuid: bookUid },
          headers: { 'accept': 'application/json' },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || '책 정보를 가져오는데 실패했습니다.');
      }

      const data = response.data.data;
      if (!data || !data.total_page) {
        throw new Error('책 정보가 없습니다.');
      }

      return { totalPage: data.total_page, myPageIdx: (data as any).my_page_idx ?? 0 };
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * 전체 페이지를 한 번에 로드합니다.
   * book-info API에서 total_page를 먼저 조회한 뒤 end_page_index로 사용합니다.
   * @param bookUid 책의 고유 ID
   * @returns 전체 페이지 배열과 마지막 페이지 인덱스
   */
  getAllPages: async (
    bookUid: string
  ): Promise<{ pages: ApiPageData[]; lastPageIndex: number }> => {
    const { totalPage } = await bookService.getBookInfo(bookUid);
    const batchSize = PAGE_LOAD_CONFIG.BATCH_SIZE;
    const startPage = PAGE_LOAD_CONFIG.DEFAULT_START_PAGE;

    if (totalPage <= batchSize) {
      return bookService.getPagesInfo(bookUid, startPage, totalPage);
    }

    const allPages: ApiPageData[] = [];
    let lastPageIndex = totalPage;

    const batches: { start: number; end: number }[] = [];
    for (let i = startPage; i <= totalPage; i += batchSize) {
      batches.push({ start: i, end: Math.min(i + batchSize - 1, totalPage) });
    }

    const results = await Promise.all(
      batches.map(({ start, end }) =>
        bookService.getPagesInfo(bookUid, start, end)
      )
    );

    for (const result of results) {
      allPages.push(...result.pages);
      lastPageIndex = result.lastPageIndex;
    }

    return { pages: allPages, lastPageIndex };
  },

  /**
   * 책의 진행 상태를 가져옵니다.
   * @param bookUid 책의 고유 ID
   * @returns 진행 상태 (page_idx)
   */
  getProgress: async (bookUid: string): Promise<number> => {
    // console.log('[API] getProgress Request:', {
    //   book_uuid: bookUid,
    // });

    try {
      const response = await apiClient.get<ApiProgressResponse>(
        `/viewer/books/${bookUid}/progress`,
        {
          headers: {
            'accept': 'application/json',
          },
        }
      );

      // console.log('[API] getProgress Response:', {
      //   success: response.data.success,
      //   error: response.data.error,
      //   page_idx: response.data.data?.page_idx,
      // });

      if (!response.data.success) {
        throw new Error(response.data.error || '진행 상태를 가져오는데 실패했습니다.');
      }

      return response.data.data?.page_idx || 1;
    } catch (error: any) {
      // console.error('[API] getProgress failed:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },

  /**
   * 책의 진행 상태를 업데이트합니다.
   * @param bookUid 책의 고유 ID
   * @param pageNo 페이지 번호
   */
  updateProgress: async (bookUid: string, pageNo: number): Promise<void> => {
    // console.log('[API] updateProgress Request:', {
    //   book_uuid: bookUid,
    //   page_idx: pageNo,
    // });

    try {
      const response = await apiClient.put<ApiProgressResponse>(
        `/viewer/books/${bookUid}/progress`,
        {
          page_idx: pageNo,
        },
        {
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }
      );

      // console.log('[API] updateProgress Response:', {
      //   success: response.data.success,
      //   error: response.data.error,
      //   page_idx: response.data.data?.page_idx,
      // });

      if (!response.data.success) {
        throw new Error(response.data.error || '진행 상태를 업데이트하는데 실패했습니다.');
      }
    } catch (error: any) {
      // console.error('[API] updateProgress failed:', {
      //   message: error.message,
      //   response: error.response?.data,
      //   status: error.response?.status,
      // });
      throw error;
    }
  },
};






